/**
 * The two sources a table can be synced from, and what each needs to be told.
 *
 * A typed const map rather than a registry, for the reason every other one here
 * is: a third source added to the backend fails to compile until this answers
 * for it, which is the only way a form built from a fixed list stays in step
 * with a list that is not.
 *
 * The premium sources -- Jira, GitHub, Hubspot and a table of this instance's
 * own -- went with the premium tree, so these two are the whole of it.
 */
import type { DataSyncTypeName } from '@/api/types'

/**
 * Every setting either source takes.
 *
 * Written out rather than left as `string`, so that the label for one is a
 * typed i18n key: a setting added below with no label fails to compile, rather
 * than reaching the form as a box with `dataSync.settings.whatever` above it.
 */
export type DataSyncSettingName =
  | 'ical_url'
  | 'postgresql_host'
  | 'postgresql_port'
  | 'postgresql_database'
  | 'postgresql_schema'
  | 'postgresql_table'
  | 'postgresql_username'
  | 'postgresql_password'
  | 'postgresql_sslmode'
  | 'assets_file_id'
  | 'sheet_name'

/** One thing a source has to be told, and how to ask for it. */
export interface DataSyncSetting {
  /** The name the endpoint takes, which is also the i18n key's last segment. */
  name: DataSyncSettingName
  kind: 'text' | 'number' | 'password' | 'choice'
  required: boolean
  choices?: readonly string[]
}

const SSL_MODES = [
  'disable',
  'allow',
  'prefer',
  'require',
  'verify-ca',
  'verify-full',
] as const

export const DATA_SYNC_SETTINGS = {
  ical_calendar: [{ name: 'ical_url', kind: 'text', required: true }],
  postgresql: [
    { name: 'postgresql_host', kind: 'text', required: true },
    { name: 'postgresql_port', kind: 'number', required: false },
    { name: 'postgresql_database', kind: 'text', required: true },
    { name: 'postgresql_schema', kind: 'text', required: false },
    { name: 'postgresql_table', kind: 'text', required: true },
    { name: 'postgresql_username', kind: 'text', required: true },
    // Set and never read: the backend keeps it out of every response on
    // purpose. So the box is always empty when the dialog opens, and an empty
    // box means "leave it as it is" rather than "clear it".
    { name: 'postgresql_password', kind: 'password', required: false },
    {
      name: 'postgresql_sslmode',
      kind: 'choice',
      required: false,
      choices: SSL_MODES,
    },
  ],
  assets_spreadsheet: [
    { name: 'assets_file_id', kind: 'number', required: true },
    { name: 'sheet_name', kind: 'text', required: true },
  ],
} as const satisfies Record<DataSyncTypeName, readonly DataSyncSetting[]>

export function settingsFor(type: DataSyncTypeName): readonly DataSyncSetting[] {
  return DATA_SYNC_SETTINGS[type]
}

/**
 * A secret is left alone when nothing was typed into it.
 *
 * The one asymmetric field in the form: everything else round-trips, so what is
 * on screen is what will be stored. A password never comes back, so sending the
 * empty box would clear a working connection every time somebody changed the
 * port.
 */
export function isSecret(setting: DataSyncSetting): boolean {
  return setting.kind === 'password'
}

/** What to send for a form, with the untouched secrets left out. */
export function settingsBody(
  type: DataSyncTypeName,
  values: Readonly<Record<string, string>>,
): Record<string, unknown> {
  const body: Record<string, unknown> = {}

  for (const setting of settingsFor(type)) {
    const typed = values[setting.name] ?? ''
    if (isSecret(setting) && typed === '') continue
    if (setting.kind === 'number') {
      // An empty number box is "no port given", which is not the same as zero.
      if (typed !== '') body[setting.name] = Number(typed)
      continue
    }
    body[setting.name] = typed
  }

  return body
}

/** What a sync has been doing, as the one sentence worth putting on screen. */
export type SyncState =
  { kind: 'never' } | { kind: 'failed'; error: string } | { kind: 'synced'; at: string }

export function syncState(sync: {
  last_sync?: string | null
  last_error?: string | null
}): SyncState {
  // The error first, and it stays until a run succeeds: `last_sync` holds when
  // the last *successful* run was, so a sync that has been broken for a week
  // still carries last week's timestamp and reporting only that would say it is
  // working.
  if (sync.last_error != null && sync.last_error !== '') {
    return { kind: 'failed', error: sync.last_error }
  }
  if (sync.last_sync == null) return { kind: 'never' }
  return { kind: 'synced', at: sync.last_sync }
}
